// =============================================================================
// selectors/backtestBoard.ts — the Results board's seam. #58.
// -----------------------------------------------------------------------------
// `ui/` may not import `domain/` at all, so every number and every row the board
// draws arrives through here.
//
// ── ⚠ WHAT THIS REPLACES ──
//
// In the old client the board is a SEPARATE PAGE fed one `postMessage` from the
// app, and `backtestsToDashboardData` (app/index.html:31700) is the adapter that
// builds that payload. Folding the board into a tab deletes the iframe, the
// message, the height reporting and the ready handshake — about 55 lines of
// plumbing that exists only because the two halves were separate documents.
//
// What survives is the adapter, and it lives here.
//
// ── ⚠ THE ONLY FILTER IS COMPLETENESS ──
//
// A sample with no derivable points is skipped, exactly as beta does. There is
// NO date range, NO account scope and NO global filter on this board — backtests
// are not trades and are not scoped to an account. That is not an omission.
// =============================================================================

import {
  boardStats, cumulativeSeries, groupBy, maxDrawdown, stackingLadder,
  underwaterSeries, curveBuckets,
  type BoardGroup, type BoardSample, type BoardStats, type CurveBucket, type TimeSlice,
} from '@domain/backtestBoard';
import { backtestContractRoot } from '@domain/backtestContract';
import type { ResolvedMark } from '@domain/confluenceEdge';
import { backtestDerived } from '@domain/backtests';
import type { BacktestRecord } from '@io/sync/backtests';
import type { PatternSummary } from '@io/sync/patterns';

export type { BoardSample, BoardStats, BoardGroup, CurveBucket, TimeSlice };
export {
  boardStats, cumulativeSeries, maxDrawdown, underwaterSeries, stackingLadder, groupBy,
  curveBuckets,
};
export { backtestContractRoot };

/** Beta's session list, in its order. The Timing card's rows. */
export const BOARD_SESSIONS = ['Asia', 'EU', 'NY-AM', 'NY-PM'] as const;

/**
 * Turn stored backtests into the board's row shape.
 *
 * ⚠ `patterns` supplies the display NAME. A sample whose pattern has been
 * deleted keeps its id and shows "(unknown)", rather than vanishing — the
 * samples still happened and still count toward the headline numbers.
 */
export function toBoardSamples(
  records: readonly BacktestRecord[],
  patterns: readonly PatternSummary[],
  /**
   * ── 🚨 THE SHARED CONFLUENCE READER, AND IT IS NOT OPTIONAL ──
   *
   * This file used to read the confluence column itself:
   * `Object.entries(b.confluences ?? {}).filter(([, v]) => v?.active === true)`
   * — no registry, no category check, and missing the legacy bare-boolean form
   * that every other reader accepts. A THIRD interpretation of one column.
   *
   * Two consequences, both on screen: the board printed internal codes where
   * the rest of the app printed labels, and a heading counted alongside its own
   * sub — one observation twice — which also pushed the sample a rung up the
   * stacking ladder the trader draws a conclusion from.
   *
   * ⚠ PASSED IN rather than built here, the way `confluenceEdge` takes its
   * marks function: this layer has no business reaching for preferences, and
   * one reader per registry is built once by the tab.
   */
  confMarks: (stored: unknown) => readonly ResolvedMark[],
): BoardSample[] {
  const nameById = new Map(patterns.map((p) => [p.id, p.name]));
  const out: BoardSample[] = [];

  for (const b of records) {
    const d = backtestDerived(b);
    // ⚠ Beta's only exclusion: an incomplete record has no points and cannot
    // contribute to anything on this board.
    if (d.points == null) continue;

    out.push({
      date: b.date,
      patternId: b.patternId,
      patternName: nameById.get(b.patternId) ?? '(unknown)',
      instrument: backtestContractRoot(b.contract),
      session: b.session,
      // ⚠ Only the ACTIVE ones count, and a key present with `active: false`
      // was considered and rejected by the trader — not the same as absent.
      // That rule, both stored shapes, the category drop and the orphan flag
      // all live in the reader now.
      confluences: confMarks(b.confluences),
      points: +d.points.toFixed(2),
      // 🚨 NULL PASSES THROUGH. Beta substitutes 0 here and the board averages
      // it. See domain/backtestBoard.ts — this is the defect the port fixes.
      r: d.r,
      mae: Math.abs(Number(b.maePoints)) || 0,
      mfe: Math.abs(Number(b.mfePoints)) || 0,
      outcome: d.outcome === 'win' ? 'w' : d.outcome === 'loss' ? 'l' : 's',
    });
  }
  return out;
}

// ── the board's own filter state ────────────────────────────────────────────

export interface BoardFilter {
  /** A product root, or 'all'. */
  instrument: string;
  /** A pattern ID, or 'all'. ⚠ An ID, never a name — names are not unique. */
  pattern: string;
}

export const EMPTY_FILTER: BoardFilter = { instrument: 'all', pattern: 'all' };

export function applyBoardFilter(
  samples: readonly BoardSample[], f: BoardFilter,
): BoardSample[] {
  return samples.filter(
    (s) => (f.instrument === 'all' || s.instrument === f.instrument)
      && (f.pattern === 'all' || s.patternId === f.pattern),
  );
}

/**
 * The instrument options, with counts.
 *
 * ⚠ Built from the WHOLE book, not the filtered set — otherwise picking one
 * instrument removes every other option and the filter cannot be changed
 * without clearing it first. The same rule the global filter drawer holds.
 */
export function instrumentOptions(
  samples: readonly BoardSample[],
): { value: string; label: string; n: number }[] {
  const counts = new Map<string, number>();
  for (const s of samples) counts.set(s.instrument, (counts.get(s.instrument) ?? 0) + 1);
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([value, n]) => ({ value, label: value, n }));
}

/** The pattern options — same all-time rule as the instruments. */
export function patternOptions(
  samples: readonly BoardSample[],
): { value: string; label: string; n: number }[] {
  const counts = new Map<string, { label: string; n: number }>();
  for (const s of samples) {
    const cur = counts.get(s.patternId) ?? { label: s.patternName, n: 0 };
    cur.n += 1;
    counts.set(s.patternId, cur);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1].n - a[1].n || a[1].label.localeCompare(b[1].label))
    .map(([value, v]) => ({ value, label: v.label, n: v.n }));
}

// ── the panels ──────────────────────────────────────────────────────────────

/** Pattern-by-pattern, for the Playbook table. */
export function patternGroups(samples: readonly BoardSample[]): BoardGroup[] {
  return groupBy(samples, (s) => ({ key: s.patternId, label: s.patternName }));
}

/**
 * Session-by-session, for the Timing card.
 *
 * ⚠ Returned in BETA'S FIXED ORDER — Asia, EU, NY-AM, NY-PM — not in
 * first-seen or best-first order. The sessions run in that sequence through the
 * day and a reader compares them against the clock, not against each other.
 *
 * ⚠ A sample saved with no session is dropped. Beta buckets by exact match
 * against the same list, so a blank silently falls out there too; making it
 * visible would be a behaviour change on a parity port. See the note in the
 * component for what the card says about it.
 */
export function sessionGroups(samples: readonly BoardSample[]): BoardGroup[] {
  const groups = groupBy(samples, (s) => (s.session ? { key: s.session, label: s.session } : null));
  return BOARD_SESSIONS
    .map((k) => groups.find((g) => g.key === k))
    .filter((g): g is BoardGroup => g !== undefined);
}

/** Confluence-by-confluence, best win rate first — the Confluence card's top half. */
export function confluenceGroups(samples: readonly BoardSample[]): BoardGroup[] {
  const map = new Map<string, { label: string; rows: BoardSample[] }>();
  for (const s of samples) {
    // ⚠ One sample appears in EVERY confluence it carried. These groups overlap
    // by construction and their counts do not sum to the book — the card asks
    // "when this was present, how did it go", not "how do the samples split".
    for (const c of s.confluences) {
      const g = map.get(c.id) ?? { label: c.name, rows: [] };
      g.rows.push(s);
      map.set(c.id, g);
    }
  }
  // ⚠ KEYED ON THE ID, LABELLED WITH THE NAME, and the split matters. This read
  // `{ key, label: key }` — the internal code on screen. Grouping by the label
  // instead would be the opposite error: two confluences a trader happened to
  // name the same would silently merge into one row.
  return [...map.entries()]
    .map(([key, g]) => ({
      key, label: g.label, stats: boardStats(g.rows), samples: g.rows,
    }))
    .sort((a, b) => (b.stats.winRate ?? -1) - (a.stats.winRate ?? -1));
}

/** Net points per calendar day — the Daily card. */
export function dailyNet(samples: readonly BoardSample[]): Map<string, { net: number; n: number }> {
  const out = new Map<string, { net: number; n: number }>();
  for (const s of samples) {
    if (!s.date) continue;
    const cur = out.get(s.date) ?? { net: 0, n: 0 };
    cur.net = +(cur.net + s.points).toFixed(2);
    cur.n += 1;
    out.set(s.date, cur);
  }
  return out;
}
